import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useDispatch } from 'react-redux';
import { SoftwarePlan } from '../../services/oprations/Payment';
import { useTheme } from '../../Context/themeContext';

const SoftwarePlanPayment = () => {
    const dispatch = useDispatch();
    const { theme } = useTheme();
    
    // State for selected plan
    const [selectedPlan, setSelectedPlan] = useState('1month');

    // State for form fields
    const [formData, setFormData] = useState({
        name: '',
        organizationName: '',
        phone: '',
        email: '',
        amount: '',
    });

    // State for validation errors
    const [errors, setErrors] = useState({
        phone: '',
        email: ''
    });

    // State to track current step (1: plan + details, 2: QR payment)
    const [step, setStep] = useState(1);

    // Theme-aware classes
    const isDark = theme === 'dark';
    
    const bgPrimary = isDark 
        ? 'bg-gradient-to-br from-blue-900 via-gray-900 to-black'
        : 'bg-gradient-to-br from-blue-50 via-gray-50 to-white';
    
    const bgCard = isDark ? 'bg-gray-800/50' : 'bg-white/80';
    const bgInput = isDark ? 'bg-gray-700' : 'bg-white';
    //const bgQR = isDark ? 'bg-gray-800/50' : 'bg-white/80';
    const bgInfo = isDark ? 'bg-blue-900/30' : 'bg-blue-100/50';
    
    const borderColor = isDark ? 'border-gray-700' : 'border-gray-200';
    const borderFocus = isDark ? 'focus:ring-blue-500 focus:border-transparent' : 'focus:ring-blue-500 focus:border-blue-500';
    const borderError = isDark ? 'border-red-500' : 'border-red-400';
    const borderInput = isDark ? 'border-gray-600' : 'border-gray-300';
    
    const textPrimary = isDark ? 'text-white' : 'text-gray-900';
    const textSecondary = isDark ? 'text-gray-300' : 'text-gray-700';
    //const textMuted = isDark ? 'text-gray-400' : 'text-gray-500';
    const textLight = isDark ? 'text-gray-400' : 'text-gray-500';
    const textError = isDark ? 'text-red-400' : 'text-red-600';
    const textSuccess = isDark ? 'text-blue-400' : 'text-blue-600';
    //const textBlue = isDark ? 'text-blue-400' : 'text-blue-600';
    
    const placeholderColor = isDark ? 'placeholder-gray-400' : 'placeholder-gray-400';
    const shadowColor = isDark ? 'shadow-xl' : 'shadow-lg';
    
    const buttonPrimary = isDark 
        ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
        : 'bg-blue-500 hover:bg-blue-600 text-white focus:ring-blue-400';
    const buttonSecondary = isDark
        ? 'bg-gray-700 hover:bg-gray-600 text-white'
        : 'bg-gray-200 hover:bg-gray-300 text-gray-800';

    // Plan details
    const plans = {
        '1month': { name: '1 Month Plan', price: 1, duration: 'month' },
        '6month': { name: '6 Months Plan', price: 2, duration: '6 months' },
        '1year': { name: '1 Year Plan', price: 3, duration: 'year' },
    };

    const currentPlan = plans[selectedPlan];
    const totalAmount = currentPlan.price;
    // eslint-disable-next-line
    const formattedAmount = `₹${totalAmount.toFixed(2)}`;

    // Phone number validation function
    const validatePhoneNumber = (phone) => {
        const cleanedPhone = phone.replace(/\D/g, '');
        const phoneRegex = /^[6-9]\d{9}$|^\+\d{1,3}\d{10}$|^\d{10}$/;
        
        if (!phone) {
            return 'Phone number is required';
        }
        if (!phoneRegex.test(cleanedPhone) && !phoneRegex.test(phone)) {
            return 'Please enter a valid phone number (10 digits or international format)';
        }
        return '';
    };

    // Email validation function
    const validateEmail = (email) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        
        if (!email) {
            return 'Email address is required';
        }
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address (e.g., name@example.com)';
        }
        return '';
    };

    // Handle plan selection
    // eslint-disable-next-line
    const handlePlanChange = (plan) => {
        setSelectedPlan(plan);
    };

    // Handle input changes with real-time validation
    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: ''
            }));
        }

        if (name === 'phone') {
            const phoneError = validatePhoneNumber(value);
            setErrors((prev) => ({
                ...prev,
                phone: phoneError
            }));
        }
        
        if (name === 'email') {
            const emailError = validateEmail(value);
            setErrors((prev) => ({
                ...prev,
                email: emailError
            }));
        }
    };

    // Format phone number as user types
    const formatPhoneNumber = (value) => {
        const cleaned = value.replace(/\D/g, '');
        
        if (cleaned.length === 10) {
            return cleaned.replace(/(\d{5})(\d{5})/, '$1 $2');
        }
        if (cleaned.length > 10 && cleaned.startsWith('91')) {
            const withoutCountry = cleaned.slice(2);
            if (withoutCountry.length === 10) {
                return `+91 ${withoutCountry.replace(/(\d{5})(\d{5})/, '$1 $2')}`;
            }
        }
        return value;
    };

    // Handle phone input with formatting
    const handlePhoneChange = (e) => {
        const rawValue = e.target.value;
        const formattedValue = formatPhoneNumber(rawValue);
        
        setFormData((prev) => ({
            ...prev,
            phone: formattedValue
        }));

        const phoneError = validatePhoneNumber(rawValue);
        setErrors((prev) => ({
            ...prev,
            phone: phoneError
        }));
    };

    // Handle Next button click with enhanced validation
    const handleNextClick = () => {
        const phoneError = validatePhoneNumber(formData.phone);
        const emailError = validateEmail(formData.email);
        
        const newErrors = {
            phone: phoneError,
            email: emailError
        };
        
        setErrors(newErrors);
        // eslint-disable-next-line
        const hasErrors = Object.values(newErrors).some(error => error !== '');
        
        if (!formData.name) {
            alert('Please enter your full name');
            return;
        }
        
        if (phoneError) {
            alert(phoneError);
            return;
        }
        
        if (emailError) {
            alert(emailError);
            return;
        }
        
        const cleanedPhone = formData.phone.replace(/\D/g, '');
        
        let payload = {
            name: formData.name,
            orgname: formData.organizationName,
            phone: cleanedPhone,
            email: formData.email,
            amount: formData.amount
        };
        console.log("payload", payload);
        dispatch(SoftwarePlan(payload));
        setStep(2);
    };

    // Handle back button
    const handleBackClick = () => {
        setStep(1);
    };

    // QR Code value for payment
    const qrValue = `upi://pay?pa=7905226299@hdfc&pn=Bandy&Moot Pvt.Ltd&am=${formData.amount || totalAmount}&cu=INR`;
    
    return (
        <div className={`min-h-screen ${bgPrimary} transition-colors duration-300`}>
            <div className="container px-4 py-8 mx-auto md:py-20">
                {/* Header */}
                <div className="mt-4 mb-10 text-center">
                    <h1 className="text-4xl font-bold text-transparent md:text-4xl bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">
                        Recharge your product
                    </h1>
                </div>

                <div className="max-w-4xl mx-auto">
                    {step === 1 ? (
                        /* Step 1: Plan Selection + User Details */
                        <div className="space-y-8">
                            {/* User Details Form */}
                            <div className={`p-6 border ${borderColor} ${shadowColor} ${bgCard} backdrop-blur-sm rounded-2xl md:p-8 transition-colors duration-300`}>
                                <h2 className={`mb-6 text-2xl font-semibold ${textSuccess}`}>Fill the required Information</h2>
                                <div className="space-y-5">
                                    {/* Name */}
                                    <div>
                                        <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                            Full Name <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className={`w-full px-4 py-3 ${textPrimary} ${placeholderColor} ${bgInput} border ${borderInput} rounded-lg focus:outline-none focus:ring-2 ${borderFocus} transition-colors duration-300`}
                                            placeholder="John Doe"
                                            required
                                        />
                                    </div>

                                    {/* Organization Name */}
                                    <div>
                                        <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                            Organization Name <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="organizationName"
                                            value={formData.organizationName}
                                            onChange={handleChange}
                                            className={`w-full px-4 py-3 ${textPrimary} ${placeholderColor} ${bgInput} border ${borderInput} rounded-lg focus:outline-none focus:ring-2 ${borderFocus} transition-colors duration-300`}
                                            placeholder="Acme Inc."
                                        />
                                    </div>

                                    {/* Phone with validation */}
                                    <div>
                                        <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                            Phone Number <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handlePhoneChange}
                                            className={`w-full px-4 py-3 ${bgInput} border rounded-lg focus:outline-none focus:ring-2 ${borderFocus} ${textPrimary} ${placeholderColor} transition-colors duration-300 ${
                                                errors.phone ? borderError : borderInput
                                            }`}
                                            placeholder="9876543210 or +91 98765 43210"
                                            required
                                        />
                                        {errors.phone && (
                                            <p className={`mt-1 text-sm ${textError}`}>{errors.phone}</p>
                                        )}
                                        <p className={`mt-1 text-xs ${textLight}`}>
                                            Enter 10-digit Indian number or international format
                                        </p>
                                    </div>

                                    {/* Email with validation */}
                                    <div>
                                        <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                            Email Address <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`w-full px-4 py-3 ${bgInput} border rounded-lg focus:outline-none focus:ring-2 ${borderFocus} ${textPrimary} ${placeholderColor} transition-colors duration-300 ${
                                                errors.email ? borderError : borderInput
                                            }`}
                                            placeholder="john@example.com"
                                            required
                                        />
                                        {errors.email && (
                                            <p className={`mt-1 text-sm ${textError}`}>{errors.email}</p>
                                        )}
                                    </div>
                                    
                                    {/* Amount */}
                                    <div>
                                        <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                            Amount <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            className={`w-full px-4 py-3 ${textPrimary} ${placeholderColor} ${bgInput} border ${borderInput} rounded-lg focus:outline-none focus:ring-2 ${borderFocus} transition-colors duration-300`}
                                            placeholder="500"
                                            required
                                        />
                                    </div>

                                    {/* Plan Summary */}
                                    <div className={`pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                        <div className="flex items-center justify-between mt-2">
                                            <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Total Amount:</span>
                                            <span className={`text-2xl font-bold ${textSuccess}`}>{formData.amount || '₹0'}</span>
                                        </div>
                                    </div>

                                    {/* Next Button */}
                                    <button
                                        onClick={handleNextClick}
                                        className={`w-full ${buttonPrimary} font-bold py-3 px-6 rounded-lg transition duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDark ? 'focus:ring-offset-gray-800' : 'focus:ring-offset-white'}`}
                                    >
                                        Next → Proceed to Payment
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Step 2: QR Code Payment */
                        <div className={`p-6 border ${borderColor} ${shadowColor} ${bgCard} backdrop-blur-sm rounded-2xl md:p-10 transition-colors duration-300`}>
                            {/* Order Summary */}
                            <div className="mb-8 text-center">
                                <h2 className={`text-2xl font-semibold ${textSuccess}`}>Complete Your Payment</h2>
                                <p className={`mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Scan QR code to pay securely</p>
                            </div>

                            <div className="flex flex-col items-center justify-center gap-8 md:flex-row">
                                {/* Left Side - Order Details */}
                                <div className={`flex-1 p-6 border ${borderColor} ${isDark ? 'bg-gray-900/50' : 'bg-gray-50/50'} rounded-xl transition-colors duration-300`}>
                                    <h3 className={`mb-4 text-lg font-semibold ${textPrimary}`}>Order Summary</h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Name:</span>
                                            <span className={`font-medium ${textPrimary}`}>{formData.name}</span>
                                        </div>
                                        {formData.organizationName && (
                                            <div className="flex justify-between">
                                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Organization:</span>
                                                <span className={textPrimary}>{formData.organizationName}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between">
                                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Email:</span>
                                            <span className={textPrimary}>{formData.email}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Phone:</span>
                                            <span className={textPrimary}>{formData.phone}</span>
                                        </div>
                                        <div className={`pt-3 mt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                            <div className="flex justify-between">
                                                <span className={`text-lg font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total Amount:</span>
                                                <span className={`text-2xl font-bold ${textSuccess}`}>{formData.amount || '₹0'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side - QR Code */}
                                <div className="flex flex-col items-center flex-1">
                                    <div className="p-5 bg-white shadow-xl rounded-2xl">
                                        <QRCodeSVG
                                            value={qrValue}
                                            size={220}
                                            bgColor="#ffffff"
                                            fgColor="#1e3a8a"
                                            level="H"
                                            includeMargin={true}
                                        />
                                    </div>

                                    <div className="mt-6 text-center">
                                        <p className={`text-lg font-bold ${textPrimary}`}>
                                            Pay Amount: <span className={textSuccess}>{formData.amount || '₹0'}</span>
                                        </p>
                                        <div className={`p-4 mt-4 border rounded-lg ${bgInfo} ${isDark ? 'border-blue-500/30' : 'border-blue-300'} transition-colors duration-300`}>
                                            <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                                📧 When you done payment, then take screenshot receipt and sent to:<br />
                                                <span className={`font-semibold ${textSuccess}`}>sales@bandy-moot.com</span>
                                            </p>
                                            <p className={`mt-2 text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                                📱 And Whatsapp to:<br />
                                                <span className={`font-semibold ${textSuccess}`}>+91 7905226299</span>
                                            </p>
                                        </div>

                                        <div className="flex justify-center gap-3 mt-6">
                                            <button
                                                onClick={handleBackClick}
                                                className={`px-6 py-2 transition duration-200 rounded-lg ${buttonSecondary}`}
                                            >
                                                ← Back
                                            </button>
                                        </div>

                                        <p className={`mt-4 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Scan QR code with any UPI app (Google Pay, PhonePe, Paytm, etc.)
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SoftwarePlanPayment;
